/**
 * ProgramCard - Reusable component for displaying program items
 * Extracted from ActiveProgramsSection to enable proper hook usage
 */
import * as React from 'react';
import { useMemo } from 'react';
import styled from '@emotion/styled';
import { useNavigate } from 'react-router-dom';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLanguage } from '../../Hooks/useLanguage';
import { useFitnessProgramTemplate } from '../../Hooks/useProgramQueries';
import { Theme } from '../../Theme/Theme';
import type { CoachProgram } from '../../Model/Coach/Program/CoachProgram';
import type { MindfulnessProgram } from '../../Model/Coach/Mindfulness/MindfulnessProgram';
import type { FitnessProgramTemplate } from '../../Model/Coach/Program/Template/FitnessProgramTemplate';
import { getMediaSmallest } from '../../Model/Media/Media';

type ProgramType = 'fitness' | 'mindfulness';

const ProgramItem = styled.div<{ $type: ProgramType }>`
  display: flex;
  gap: 16px;
  padding: 16px;
  background: ${Theme.Colors.White};
  border-radius: 12px;
  border: 1px solid #efefef;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(props) => (props.$type === 'fitness' ? Theme.Colors.PrimaryGreen : Theme.Colors.DarkGray)};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ProgramImage = styled.div<{ $type: ProgramType }>`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background: ${(props) =>
    props.$type === 'fitness' ? 'linear-gradient(135deg, #00dda2 0%, #00AF96 100%)' : 'linear-gradient(135deg, #3C3C3C 0%, #5A5A5A 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  i {
    font-size: 32px;
    color: ${Theme.Colors.White};
  }
`;

const ProgramInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ProgramMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgramType = styled.span<{ $type: ProgramType }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.$type === 'fitness' ? Theme.Colors.PrimaryGreen : Theme.Colors.DarkGray)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ProgramName = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #191919;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProgramProgress = styled.div`
  font-size: 14px;
  color: ${Theme.Colors.MediumGray};
`;

const ProgressBar = styled.div`
  height: 6px;
  background: ${Theme.Colors.LightGray};
  border-radius: 3px;
  overflow: hidden;
  margin-top: 4px;
`;

const ProgressFill = styled.div<{ $percent: number; $type: ProgramType }>`
  height: 100%;
  width: ${(props) => props.$percent}%;
  background: ${(props) => (props.$type === 'fitness' ? Theme.Colors.PrimaryGreen : Theme.Colors.DarkGray)};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

type FitnessProgramCardProps = {
  program: CoachProgram;
};

export const FitnessProgramCard: React.FC<FitnessProgramCardProps> = ({ program }) => {
  const { t } = useTrans();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const { data: fetchedTemplate } = useFitnessProgramTemplate(program.superMacroTemplateId);
  const template = fetchedTemplate || program.template;

  const templateName = useMemo(() => {
    if (!template) return 'Program';
    const name = template.name ?? (template as any).getName?.(language);
    if (typeof name === 'string') return name;
    if (Array.isArray(name)) {
      const langEntry = name.find((n: any) => n.lang === language);
      return langEntry?.value ?? name[0]?.value ?? 'Program';
    }
    return 'Program';
  }, [template, language]);

  const templateImage = useMemo(() => {
    const img = template?.image || (template as FitnessProgramTemplate)?.medias?.[0];
    return img ? getMediaSmallest(img) : undefined;
  }, [template]);

  const { progress, progressText } = useMemo(() => {
    const { startDateTime, endDateTime } = program;
    if (!startDateTime || !endDateTime) {
      return { progress: 0, progressText: t('program.active', { defaultValue: 'Active' }) };
    }

    const now = new Date();
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    const progressPercent = Math.min(Math.max((elapsed / total) * 100, 0), 100);

    const daysRemaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const progressMsg =
      daysRemaining > 0
        ? t('program.daysRemaining', { defaultValue: '{{days}} days left', days: daysRemaining })
        : t('program.completed', { defaultValue: 'Completed' });

    return { progress: progressPercent, progressText: progressMsg };
  }, [program, t]);

  return (
    <ProgramItem $type="fitness" onClick={() => navigate(`/account/program/fitness/${program.id}`)}>
      <ProgramImage $type="fitness">
        {templateImage ? <img src={templateImage} alt={templateName} /> : <i className="bi bi-lightning-charge-fill" />}
      </ProgramImage>
      <ProgramInfo>
        <ProgramMeta>
          <ProgramType $type="fitness">{t('program.type.fitness', { defaultValue: 'Fitness' })}</ProgramType>
        </ProgramMeta>
        <ProgramName>{templateName}</ProgramName>
        <ProgramProgress>{progressText}</ProgramProgress>
        <ProgressBar>
          <ProgressFill $percent={progress} $type="fitness" />
        </ProgressBar>
      </ProgramInfo>
    </ProgramItem>
  );
};

type MindfulnessProgramCardProps = {
  program: MindfulnessProgram;
};

export const MindfulnessProgramCard: React.FC<MindfulnessProgramCardProps> = ({ program }) => {
  const { t } = useTrans();
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const template = program.template;

  const templateName = useMemo(() => {
    if (!template) return 'Program';
    const name = template.name ?? (template as any).getName?.(language);
    if (typeof name === 'string') return name;
    if (Array.isArray(name)) {
      const langEntry = name.find((n: any) => n.lang === language);
      return langEntry?.value ?? name[0]?.value ?? 'Program';
    }
    return 'Program';
  }, [template, language]);

  const templateImage = useMemo(() => {
    const img = template?.images?.[0];
    return img ? getMediaSmallest(img) : undefined;
  }, [template]);

  const { progress, progressText } = useMemo(() => {
    const { startDateTime, endDateTime } = program;
    if (!startDateTime || !endDateTime) {
      return { progress: 0, progressText: t('program.active', { defaultValue: 'Active' }) };
    }

    const now = new Date();
    const start = new Date(startDateTime);
    const end = new Date(endDateTime);
    const total = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    const progressPercent = Math.min(Math.max((elapsed / total) * 100, 0), 100);

    const daysRemaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const progressMsg =
      daysRemaining > 0
        ? t('program.daysRemaining', { defaultValue: '{{days}} days left', days: daysRemaining })
        : t('program.completed', { defaultValue: 'Completed' });

    return { progress: progressPercent, progressText: progressMsg };
  }, [program, t]);

  return (
    <ProgramItem $type="mindfulness" onClick={() => navigate(`/account/program/mindfulness/${program.id}`)}>
      <ProgramImage $type="mindfulness">
        {templateImage ? <img src={templateImage} alt={templateName} /> : <i className="bi bi-moon-stars-fill" />}
      </ProgramImage>
      <ProgramInfo>
        <ProgramMeta>
          <ProgramType $type="mindfulness">{t('program.type.mindfulness', { defaultValue: 'Mindfulness' })}</ProgramType>
        </ProgramMeta>
        <ProgramName>{templateName}</ProgramName>
        <ProgramProgress>{progressText}</ProgramProgress>
        <ProgressBar>
          <ProgressFill $percent={progress} $type="mindfulness" />
        </ProgressBar>
      </ProgramInfo>
    </ProgramItem>
  );
};
